import type { Schema } from '@cdn/api';
import { Pointer, type StreamEvent } from '@cdn/contracts';
import type { Handlers, Subscription, Transport } from '../port';

/**
 * UNVALIDATED until it runs against a Supabase stack: it was written from the contract and the Supabase
 * Realtime documentation only.
 *
 * Joins a private Broadcast channel with the short-lived token of a realtime grant from
 * `POST /v1/realtime/grants`. The grant also supplies the project URL and its public key; no service-role key
 * ever reaches the browser. A channel name grants nothing: the service authorizes the join.
 *
 * Two broadcast events arrive on the channel: the microbatch, and `beyond-cdn-pointer/1`, which the notifier
 * sends instead of events that are too large or too late to travel. Both go to the stream unchanged; the
 * contracts' `Replay` turns a pointer into a gap, which the stream closes through the replay route.
 */
export class BroadcastTransport implements Transport {
	readonly name = 'supabase-broadcast';
	readonly validated = false;

	open(channel: string, handlers: Handlers, grant?: Schema.RealtimeGrant): Subscription {
		const { url, key, event = 'beyond-cdn-events/1' } = grant?.transport ?? {};
		if (!grant || !url || !key) {
			handlers.state('interrupted');
			return { close: () => undefined };
		}

		let closed = false;
		let release = () => {};
		let authorize = (_: string) => {};

		void import('@supabase/supabase-js').then(async ({ createClient }) => {
			if (closed) return;
			const client = createClient(url, key, { auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false } });
			await client.realtime.setAuth(grant.token);

			const joined = client.channel(channel, { config: { private: true } });
			joined.on('broadcast', { event }, message => handlers.message(message.payload as { events: StreamEvent[] }));
			joined.on('broadcast', { event: Pointer.EVENT }, message => {
				const pointer = Pointer.read(message.payload);
				pointer && handlers.message({ channel: pointer.channel, sequence: pointer.sequence, reason: pointer.reason });
			});
			joined.subscribe(status => handlers.state(status === 'SUBSCRIBED' ? 'open' : 'interrupted'));

			authorize = token => void client.realtime.setAuth(token);
			release = () => {
				void client.removeChannel(joined);
				void client.realtime.disconnect();
			};
			closed && release();
		}).catch(() => {
			// The client did not load or the channel could not be authorized: the stream recovers through the
			// replay route instead of showing a live state nothing feeds
			closed || handlers.state('interrupted');
		});

		return {
			authorize: token => authorize(token),
			close: () => {
				closed = true;
				release();
			}
		};
	}
}
